import axios from "axios";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { setUser } from "../../../store/userSlice";
import { NavLink, useNavigate } from "react-router-dom";
import { baseurl } from "../../../Baseurl";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import logo from "../../../assests/loginpageimage.jpg";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export default function PatientLogin() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [civilIdNumber, setCivilIdNumber] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [forgotPasswordErrors, setForgotPasswordErrors] = useState({});
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [userId, setUserId] = useState("");

    const validate = () => {
        const newErrors = {};
        if (!civilIdNumber) {
            newErrors.civilIdNumber = "Email or Civil ID is required";
        }
        if (!password) {
            newErrors.password = "Password is required";
        } else if (password.length < 6) {
            newErrors.password = "Password must be at least 6 characters";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateForgotPassword = () => {
        const newErrors = {};
        if (currentStep === 1) {
            if (!forgotEmail) {
                newErrors.email = "Email is required";
            } else if (!/\S+@\S+\.\S+/.test(forgotEmail)) {
                newErrors.email = "Email is invalid";
            }
        } else if (currentStep === 2) {
            if (!otp) {
                newErrors.otp = "OTP is required";
            } else if (otp.length !== 6) {
                newErrors.otp = "OTP must be 6 digits";
            }
        } else if (currentStep === 3) {
            if (!newPassword) {
                newErrors.newPassword = "New Password is required";
            } else if (newPassword.length < 6) {
                newErrors.newPassword = "Password must be at least 6 characters";
            }
            if (!confirmNewPassword) {
                newErrors.confirmNewPassword = "Confirm Password is required";
            } else if (newPassword !== confirmNewPassword) {
                newErrors.confirmNewPassword = "Passwords do not match";
            }
        }
        setForgotPasswordErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleGenerateOtp = async () => {
        if (validateForgotPassword()) {
            setForgotPasswordLoading(true);
            try {
                const requestData = {
                    email: forgotEmail,
                };
                const response = await axios.post(
                    `${baseurl}userOtpGenerate`,
                    requestData
                );
                if (response.status === 200) {
                    Swal.fire("Success", "OTP sent to your email!", "success");
                    setOtpSent(true);
                    setCurrentStep(2);
                    setForgotPasswordErrors({});
                }
            } catch (error) {
                console.error("OTP generation failed:", error);
                Swal.fire(
                    "Error",
                    error.response?.data?.message || "Failed to send OTP",
                    "error"
                );
            } finally {
                setForgotPasswordLoading(false);
            }
        }
    };

    const handleVerifyOtp = async () => {
        if (validateForgotPassword()) {
            setForgotPasswordLoading(true);
            try {
                const requestData = {
                    otp: otp,
                };
                const response = await axios.post(
                    `${baseurl}userVerifyOtp`,
                    requestData
                );
                if (response.status === 200) {
                    if (response.data.data) {
                        setUserId(response.data.data);
                    } else if (response.data.user && response.data.user.id) {
                        setUserId(response.data.user.id);
                    } else if (response.data.id) {
                        setUserId(response.data.id);
                    }
                    Swal.fire("Success", "OTP verified successfully!", "success");
                    setCurrentStep(3);
                    setForgotPasswordErrors({});
                }
            } catch (error) {
                console.error("OTP verification failed:", error);
                Swal.fire(
                    "Error",
                    error.response?.data?.message || "Invalid OTP",
                    "error"
                );
            } finally {
                setForgotPasswordLoading(false);
            }
        }
    };

    const handleResetPassword = async () => {
        if (validateForgotPassword()) {
            setForgotPasswordLoading(true);
            try {
                const requestData = {
                    newPassword: newPassword,
                    confirmNewPassword: confirmNewPassword,
                };
                const endpoint = userId
                    ? `${baseurl}userResetPassword/${userId}`
                    : `${baseurl}userResetPassword`;
                const response = await axios.post(endpoint, requestData);
                if (response.status === 200) {
                    Swal.fire("Success", "Password reset successfully!", "success");
                    setShowForgotPasswordModal(false);
                    setForgotEmail("");
                    setOtp("");
                    setNewPassword("");
                    setConfirmNewPassword("");
                    setForgotPasswordErrors({});
                    setOtpSent(false);
                    setCurrentStep(1);
                    setUserId("");
                }
            } catch (error) {
                console.error("Password reset failed:", error);
                Swal.fire(
                    "Error",
                    error.response?.data?.message || "Failed to reset password",
                    "error"
                );
            } finally {
                setForgotPasswordLoading(false);
            }
        }
    };

    // Simple login handler - will be replaced when API is ready
    const handleClick = async () => {
        if (validate()) {
            setLoading(true);

            const userData = {
                civilId: civilIdNumber,
                password: password,
            };

            try {
                const response = await axios.post(`${baseurl}patientLogin`, userData);

                if (response.data && response.data.success) {
                    const patientDetails = response.data.patient;
                    const token = response.data.token;

                    // Structure the data as expected by PatientPortal's getPatientId
                    const storageData = {
                        data: {
                            ...patientDetails,
                            role: "patient", // Ensure role is present for verification
                            userType: "patient" // Adding userType as a fallback
                        },
                        // token: response.data.token // Save token if available in the future
                    };


                    localStorage.setItem("userMedical", JSON.stringify(storageData));
                    localStorage.setItem("token", token);

                    // Dispatch to Redux if needed (optional but good practice)
                    // dispatch(setUser(storageData.data)); 

                    Swal.fire("Success!!", "Login Successfully.", "success");
                    navigate("/patient");
                } else {
                    Swal.fire("Error", response.data.message || "Login failed", "error");
                }

            } catch (error) {
                toast.error(error.response?.data?.message || "Login failed");
                console.error("Login failed:", error);
                Swal.fire("Error", error.response?.data?.message || "Login failed", "error");
            } finally {
                setLoading(false);
            }
        } else {
            console.error("Validation failed");
        }
    };

    return (
        <div className="loginMainVideo">
            <video autoPlay muted loop playsInline>
                <source src="/homeVideo.mp4" type="video/mp4" />
            </video>
            <div className="mainLogin">
                <div className="container loginBgColor">
                    <div className="login-box mx-auto bg-white">
                        <div className="text-center">
                            <img
                                src={logo}
                                alt="Logo"
                                className="logo-image mb-2"
                                style={{ width: "139px" }}
                            />
                        </div>
                        <h4 className="login-title">Patient Portal Login</h4>
                        <div className="mb-3">
                            <label className="form-label">Civil ID</label>
                            <input
                                type="text"
                                className="form-control bg-light"
                                placeholder="Enter civil ID"
                                value={civilIdNumber}
                                onChange={(e) => setCivilIdNumber(e.target.value)}
                            />
                            {errors.civilIdNumber && (
                                <small className="text-danger">{errors.civilIdNumber}</small>
                            )}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Password</label>
                            <div className="input-group">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className="form-control bg-light"
                                    placeholder="••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                >
                                    {showPassword ? <VisibilityOffIcon /> : "👁"}
                                </button>
                            </div>
                            {errors.password && (
                                <small className="text-danger">{errors.password}</small>
                            )}
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <label
                                    htmlFor="remember"
                                    className="form-check-label d-flex align-items-center gap-2"
                                >
                                    <input type="checkbox" id="remember" />
                                    Keep me sign in
                                </label>
                            </div>
                            <a
                                href="#"
                                className="small"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowForgotPasswordModal(true);
                                }}
                            >
                                Forgot Password?
                            </a>
                        </div>
                        <button
                            className="btn btn-primary w-100"
                            onClick={handleClick}
                            disabled={loading}
                        >
                            {loading ? "Logging in..." : "Login"}
                        </button>
                    </div>
                    <ToastContainer />
                </div>
                {showForgotPasswordModal && (
                    <div
                        className="modal fade show"
                        style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {currentStep === 1 && "Reset Password"}
                                        {currentStep === 2 && "Otp Verification"}
                                        {currentStep === 3 && "Set New Password"}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setShowForgotPasswordModal(false);
                                            setCurrentStep(1);
                                            setForgotEmail("");
                                            setOtp("");
                                            setNewPassword("");
                                            setConfirmNewPassword("");
                                            setForgotPasswordErrors({});
                                            setOtpSent(false);
                                            setUserId("");
                                        }}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {currentStep === 1 && (
                                        <div className="mb-3">
                                            <label className="form-label">Email Address</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                placeholder="Enter your email address"
                                                value={forgotEmail}
                                                onChange={(e) => setForgotEmail(e.target.value)}
                                            />
                                            {forgotPasswordErrors.email && (
                                                <small className="text-danger">
                                                    {forgotPasswordErrors.email}
                                                </small>
                                            )}
                                        </div>
                                    )}
                                    {currentStep === 2 && (
                                        <div className="mb-3">
                                            <label className="form-label">Enter OTP</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter 6-digit OTP"
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value)}
                                                maxLength="6"
                                            />
                                            {forgotPasswordErrors.otp && (
                                                <small className="text-danger">
                                                    {forgotPasswordErrors.otp}
                                                </small>
                                            )}
                                            <small className="text-muted">
                                                OTP has been sent to {forgotEmail}
                                            </small>
                                        </div>
                                    )}
                                    {currentStep === 3 && (
                                        <>
                                            <div className="mb-3">
                                                <label className="form-label">New Password</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    placeholder="Enter new password"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                />
                                                {forgotPasswordErrors.newPassword && (
                                                    <small className="text-danger">
                                                        {forgotPasswordErrors.newPassword}
                                                    </small>
                                                )}
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Confirm New Password
                                                </label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    placeholder="Confirm new password"
                                                    value={confirmNewPassword}
                                                    onChange={(e) =>
                                                        setConfirmNewPassword(e.target.value)
                                                    }
                                                />
                                                {forgotPasswordErrors.confirmNewPassword && (
                                                    <small className="text-danger">
                                                        {forgotPasswordErrors.confirmNewPassword}
                                                    </small>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowForgotPasswordModal(false);
                                            setCurrentStep(1);
                                            setForgotEmail("");
                                            setOtp("");
                                            setNewPassword("");
                                            setConfirmNewPassword("");
                                            setForgotPasswordErrors({});
                                            setOtpSent(false);
                                            setUserId("");
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    {currentStep === 1 && (
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleGenerateOtp}
                                            disabled={forgotPasswordLoading}
                                        >
                                            {forgotPasswordLoading ? "Sending..." : "Send OTP"}
                                        </button>
                                    )}
                                    {currentStep === 2 && (
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleVerifyOtp}
                                            disabled={forgotPasswordLoading}
                                        >
                                            {forgotPasswordLoading ? "Verifying..." : "Verify OTP"}
                                        </button>
                                    )}
                                    {currentStep === 3 && (
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleResetPassword}
                                            disabled={forgotPasswordLoading}
                                        >
                                            {forgotPasswordLoading
                                                ? "Resetting..."
                                                : "Reset Password"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
